import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Check, Zap, Shield, ArrowRight, Lock, Star, RefreshCw, CheckCircle,
  AlertCircle, Crown, Info, Tag, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { getPromoCode, clearPromoCode } from '../../hooks/usePromoCapture';

// Plans tonen launch-month + recurring split duidelijk
const PLANS = [
  {
    id: 'radar_monthly',
    name: 'Connected Self-Service',
    price: '14,95',
    suffix: '/mnd excl. btw',
    desc: 'Koppel uw accounts en krijg account-specifieke AI-tips. Maandelijks opzegbaar.',
    afterPrice: null,
  },
  {
    id: 'imperium_beheer_launch',
    name: 'Managed AI Bot',
    price: '995,95',
    suffix: 'excl. btw — eerste maand',
    desc: 'Onboarding + eerste uitvoeringsmaand. Daarna automatisch over op €2.495,95/mnd excl. btw.',
    afterPrice: 'Daarna €2.495,95/mnd excl. btw',
    badge: 'Premium',
    premium: true,
  },
];

const VAT_RATE = 0.21;

const formatEUR = (n) => `€${(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PriceBreakdown({ exVat, label, isLaunch, nextExVat }) {
  const vat = +(exVat * VAT_RATE).toFixed(2);
  const incl = +(exVat + vat).toFixed(2);
  return (
    <div data-testid="checkout-price-breakdown" style={{ background: '#f8fafc', borderRadius: '12px', padding: '14px', marginTop: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
        <span>{label}</span>
        <span>{formatEUR(exVat)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
        <span>Btw (21%)</span>
        <span>{formatEUR(vat)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 700, color: '#0f172a', borderTop: '1px solid #e2e8f0', paddingTop: '8px', marginTop: '4px' }}>
        <span>Totaal nu te betalen</span>
        <span>{formatEUR(incl)} incl. btw</span>
      </div>
      {isLaunch && nextExVat && (
        <div style={{ marginTop: '10px', padding: '10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
            ⏰ Na uw launch-maand
          </p>
          <p style={{ fontSize: '12px', color: '#92400e', lineHeight: 1.5 }}>
            U gaat automatisch over op het maandelijkse tarief: <strong>{formatEUR(nextExVat)}/mnd excl. btw</strong> ({formatEUR(+(nextExVat * 1.21).toFixed(2))} incl. btw). U kunt vóór de eerstvolgende factuur altijd opzeggen.
          </p>
        </div>
      )}
    </div>
  );
}

function SuccessView({ paymentId }) {
  const { api } = useAuth();
  const [status, setStatus] = useState('checking');
  const [info, setInfo] = useState({});
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!paymentId) return;
    const pollStatus = async () => {
      try {
        const r = await api('get', `/api/checkout/status/${paymentId}`);
        setInfo(r.data);
        if (r.data.payment_status === 'paid') {
          setStatus('paid');
          toast.success(r.data.type === 'service' ? 'Bestelling geplaatst!' : 'Betaling geslaagd!');
          return;
        }
        if (['expired', 'canceled', 'failed'].includes(r.data.payment_status)) {
          setStatus(r.data.payment_status);
          return;
        }
        if (attempts < 10) setTimeout(() => setAttempts(a => a + 1), 2000);
        else setStatus('timeout');
      } catch (e) {
        if (attempts < 10) setTimeout(() => setAttempts(a => a + 1), 2000);
        else setStatus('error');
      }
    };
    pollStatus();
  }, [paymentId, attempts]);

  if (status === 'checking') return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ width: '48px', height: '48px', margin: '0 auto 16px', border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '20px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Betaling verwerken...</h2>
      <p style={{ color: '#94a3b8', fontSize: '14px' }}>Even geduld, we controleren uw betaling.</p>
    </div>
  );

  if (status === 'paid') return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }} data-testid="payment-success">
      <div style={{ width: '64px', height: '64px', margin: '0 auto 16px', borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CheckCircle size={32} style={{ color: '#22c55e' }} />
      </div>
      <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
        {info.type === 'service' ? 'Bestelling bevestigd!' : (info.is_first_month ? 'Welkom bij Managed AI — launch maand actief!' : 'Welkom bij IMPERIA AI!')}
      </h2>
      <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px', maxWidth: '480px', margin: '0 auto 20px' }}>
        {info.is_first_month
          ? `Uw launch-maand is actief (${formatEUR(info.amount_ex_vat || 995.95)} excl. btw). Wij starten direct met onboarding, audits en uitvoering. Na 30 dagen gaat het maandelijkse tarief van €2.495,95 excl. btw in — u krijgt een waarschuwing 7 dagen vóór de overgang.`
          : 'Uw betaling is geslaagd. Alle opdrachten, analyses en monitoring zijn nu ontgrendeld.'}
      </p>
      {info.is_first_month && (
        <div data-testid="managed-website-chat-cta" style={{
          maxWidth: 480, margin: '0 auto 20px', background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
          border: '2px solid #D4A017', borderRadius: 14, padding: '18px 20px', textAlign: 'left',
        }}>
          <p style={{ fontSize: 12, color: '#92400e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
            🎉 Volgende stap
          </p>
          <p style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', margin: '4px 0 6px', fontFamily: "'Sora', sans-serif" }}>
            Vertel ons welke website u wilt
          </p>
          <p style={{ fontSize: 12, color: '#78350f', marginBottom: 12, lineHeight: 1.5 }}>
            Ga direct naar de AI website-chat. Typ in gewone taal wat u nodig heeft — onze AI stelt de goede vragen en ons team begint binnen 48 uur te bouwen.
          </p>
          <Link to="/platform/website-chat" data-testid="go-website-chat" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0f172a',
            color: 'white', padding: '10px 18px', borderRadius: 10, textDecoration: 'none', fontSize: 13, fontWeight: 700,
          }}>Open AI website-chat <ArrowRight size={14} /></Link>
        </div>
      )}
      <Link to={info.type === 'service' ? '/platform/diensten' : '/platform'} data-testid="go-to-dashboard" style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', background: info.is_first_month ? 'transparent' : '#0f172a',
        color: info.is_first_month ? '#64748b' : 'white', padding: '12px 24px', borderRadius: '10px', textDecoration: 'none', fontSize: '14px', fontWeight: 700,
        border: info.is_first_month ? '1px solid #e2e8f0' : 'none',
      }}>Naar mijn dashboard <ArrowRight size={16} /></Link>
    </div>
  );

  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <AlertCircle size={32} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
      <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
        {status === 'expired' ? 'Sessie verlopen' : 'Er ging iets mis'}
      </h2>
      <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '16px' }}>Probeer het opnieuw of neem contact op met support.</p>
      <Link to="/platform/checkout" style={{ color: '#D4A017', fontWeight: 600, fontSize: '14px' }}>Opnieuw proberen</Link>
    </div>
  );
}

export default function CheckoutModule() {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan');
  const serviceId = searchParams.get('service_id');
  const serviceName = searchParams.get('service_name');
  const servicePrice = searchParams.get('price');
  const [selectedPlan, setSelectedPlan] = useState(preselectedPlan || 'radar_monthly');
  const [loading, setLoading] = useState(false);
  const [acceptTransition, setAcceptTransition] = useState(false);
  const sessionId = searchParams.get('session_id');
  const paymentId = searchParams.get('payment_id') || sessionId;

  // ── Discount code state (must be declared BEFORE early returns — React hooks rules) ──
  const [discountInput, setDiscountInput] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [discountChecking, setDiscountChecking] = useState(false);
  const [discountAutoApplied, setDiscountAutoApplied] = useState(false);

  // ── Billing info state ──
  const [billingInfo, setBillingInfo] = useState(null);

  useEffect(() => {
    api('get', '/api/platform/billing-info')
      .then((r) => {
        setBillingInfo(r.data);
      })
      .catch(() => { /* not authed yet */ });
  }, [api]);

  // Auto-prefill vanuit localStorage of ?promo= in current URL
  useEffect(() => {
    const fromUrl = (searchParams.get('promo') || '').toUpperCase();
    const fromStorage = (getPromoCode() || '').toUpperCase();
    const code = fromUrl || fromStorage;
    if (code) {
      setDiscountInput(code);
      setDiscountAutoApplied(true);
    }
  }, [searchParams]);

  // Live validatie — debounce 400ms
  useEffect(() => {
    const code = (discountInput || '').toUpperCase().trim();
    if (!code || code.length < 4) {
      setDiscountValidation(null);
      return;
    }
    const t = setTimeout(async () => {
      setDiscountChecking(true);
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/discount-codes/validate`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, package_id: selectedPlan }),
          }
        );
        const data = await r.json();
        setDiscountValidation(data);
      } catch {
        setDiscountValidation({ valid: false, reason: 'Validatie mislukt' });
      }
      setDiscountChecking(false);
    }, 400);
    return () => clearTimeout(t);
  }, [discountInput, selectedPlan]);

  if (paymentId) return <SuccessView paymentId={paymentId} />;

  // Service checkout mode
  if (serviceId) {
    const priceNum = parseFloat((servicePrice || '0').replace(',', '.'));
    const handleServiceCheckout = async () => {
      setLoading(true);
      try {
        const r = await api('post', '/api/checkout/create-session', {
          service_id: serviceId,
          origin_url: window.location.origin,
        });
        if (r.data.url) window.location.href = r.data.url;
      } catch (e) {
        toast.error('Checkout mislukt. Probeer het opnieuw.');
        setLoading(false);
      }
    };

    return (
      <div data-testid="service-checkout" style={{ maxWidth: '520px', margin: '0 auto', padding: '20px' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#D4A017',
            color: '#0f172a', fontSize: '11px', fontWeight: 800, padding: '5px 12px', borderRadius: '8px', marginBottom: '12px',
          }}>
            <Lock size={11} /> Beveiligde betaling
          </div>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
            Dienst bestellen
          </h1>
        </div>

        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9', padding: '28px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
            {decodeURIComponent(serviceName || serviceId)}
          </h2>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
            <span style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a' }}>{formatEUR(priceNum)}</span>
            <span style={{ color: '#94a3b8', fontSize: '14px' }}>excl. btw · eenmalig</span>
          </div>
          <PriceBreakdown exVat={priceNum} label="Eenmalig bedrag (excl. btw)" />

          <button
            data-testid="pay-service-btn"
            onClick={handleServiceCheckout}
            disabled={loading}
            style={{
              marginTop: '16px',
              width: '100%', background: '#D4A017', color: '#0f172a', fontWeight: 800,
              fontSize: '15px', padding: '16px', borderRadius: '12px', border: 'none', cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}
          >
            <Lock size={14} /> {loading ? 'Bezig...' : `Betaal ${formatEUR(+(priceNum * 1.21).toFixed(2))} incl. btw`}
          </button>
        </div>
        <p style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8' }}>
          Veilig betalen via Stripe · iDEAL · Creditcard · SEPA-incasso · Bancontact
        </p>
      </div>
    );
  }

  const isPaid = user?.role === 'radar_paid' || user?.role === 'admin' || user?.role === 'beheer_paid';
  const selected = PLANS.find(p => p.id === selectedPlan) || PLANS[0];
  const isManaged = selectedPlan === 'imperium_beheer_launch';
  const priceNum = parseFloat(selected.price.replace(',', '.'));
  const needsTransitionAccept = isManaged && !acceptTransition;

  const removeDiscount = () => {
    setDiscountInput('');
    setDiscountValidation(null);
    setDiscountAutoApplied(false);
    clearPromoCode();
  };

  const effectiveExVat = discountValidation?.valid ? discountValidation.discounted_ex : priceNum;
  const effectiveLabel = isManaged ? 'Eerste maand (excl. btw)' : 'Maandelijks bedrag (excl. btw)';

  const handleCheckout = async () => {
    if (needsTransitionAccept) {
      toast.error('Bevestig eerst dat u akkoord gaat met de overgang naar €2.495,95/mnd na de launch-maand.');
      return;
    }
    if (billingInfo && !billingInfo.is_complete_for_checkout) {
      toast.error('Vul eerst uw bedrijfsgegevens aan');
      return;
    }
    setLoading(true);
    try {
      const body = {
        package_id: selectedPlan,
        origin_url: window.location.origin,
      };
      if (discountValidation?.valid && discountInput) {
        body.discount_code = discountInput.toUpperCase().trim();
      }
      const r = await api('post', '/api/checkout/create-session', body);
      if (r.data.url) {
        if (discountValidation?.valid) clearPromoCode();
        window.location.href = r.data.url;
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === 'missing_billing_info') {
        toast.error('Vul eerst aan: ' + (detail.missing_fields || []).join(', '));
        window.location.href = '/platform/billing-info';
        return;
      }
      toast.error(detail?.message || detail || 'Checkout mislukt. Probeer het opnieuw.');
      setLoading(false);
    }
  };

  if (isPaid && !preselectedPlan) return (
    <div data-testid="checkout-paid" style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ width: '64px', height: '64px', margin: '0 auto 16px', borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CheckCircle size={32} style={{ color: '#22c55e' }} />
      </div>
      <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '20px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Uw plan is actief</h2>
      <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>U heeft volledige toegang tot uw huidige plan.</p>
      <Link to="/platform" style={{ color: '#D4A017', fontWeight: 600, textDecoration: 'none' }}>Terug naar dashboard</Link>
    </div>
  );

  return (
    <div data-testid="checkout-page">
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#D4A017',
          color: '#0f172a', padding: '4px 12px', borderRadius: '20px', fontSize: '11px',
          fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px',
        }}>
          <Zap size={12} /> Upgrade
        </div>
        <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
          Kies uw plan
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '520px', margin: '0 auto' }}>
          Alle prijzen excl. btw. Btw wordt apart berekend bij checkout. Maandelijks opzegbaar.
        </p>
      </div>

      {/* Plans */}
      <div className="checkout-plans-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', maxWidth: '720px', margin: '0 auto 24px' }}>
        {PLANS.map(plan => (
          <div
            key={plan.id}
            data-testid={`plan-${plan.id}`}
            onClick={() => { setSelectedPlan(plan.id); setAcceptTransition(false); }}
            style={{
              background: plan.premium ? 'linear-gradient(135deg, #0f172a, #1e1b4b)' : 'white',
              borderRadius: '16px', padding: '20px', cursor: 'pointer',
              border: `2px solid ${selectedPlan === plan.id ? '#D4A017' : plan.premium ? 'transparent' : '#f1f5f9'}`,
              transition: 'all 0.2s', position: 'relative',
            }}
          >
            {plan.badge && (
              <div style={{
                position: 'absolute', top: '-10px', right: '14px',
                background: '#D4A017', color: '#0f172a',
                padding: '3px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: 700,
              }}>{plan.badge}</div>
            )}
            <p style={{ fontSize: '13px', fontWeight: 700, color: plan.premium ? 'rgba(255,255,255,0.7)' : '#64748b', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {plan.premium && <Crown size={13} color="#D4A017" />}
              {plan.name}
            </p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '4px' }}>
              <span style={{ fontFamily: "'Sora', sans-serif", fontSize: '26px', fontWeight: 800, color: plan.premium ? 'white' : '#0f172a' }}>
                €{plan.price}
              </span>
              <span style={{ fontSize: '11px', color: plan.premium ? 'rgba(255,255,255,0.5)' : '#94a3b8' }}>{plan.suffix}</span>
            </div>
            {plan.afterPrice && (
              <p style={{ fontSize: '11px', color: '#D4A017', fontWeight: 700, marginBottom: '6px' }} data-testid={`plan-after-${plan.id}`}>
                {plan.afterPrice}
              </p>
            )}
            <p style={{ fontSize: '11px', color: plan.premium ? 'rgba(255,255,255,0.6)' : '#94a3b8', lineHeight: 1.4 }}>{plan.desc}</p>
            <div style={{
              width: '20px', height: '20px', borderRadius: '50%', marginTop: '10px',
              border: `2px solid ${selectedPlan === plan.id ? '#D4A017' : plan.premium ? 'rgba(255,255,255,0.2)' : '#e2e8f0'}`,
              background: selectedPlan === plan.id ? '#D4A017' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {selectedPlan === plan.id && <Check size={12} color={plan.premium ? '#0f172a' : 'white'} />}
            </div>
          </div>
        ))}
      </div>

      {/* Price breakdown */}
      <div style={{ maxWidth: '600px', margin: '0 auto 16px' }}>
        <PriceBreakdown
          exVat={effectiveExVat}
          label={effectiveLabel}
          isLaunch={isManaged}
          nextExVat={isManaged ? 2495.95 : null}
        />
      </div>

      {/* Discount code input */}
      <div style={{ maxWidth: '600px', margin: '0 auto 16px' }} data-testid="discount-code-section">
        <div style={{ background: 'white', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '14px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#0f172a', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <Tag size={13} color="#D4A017" /> Kortingscode
            {discountAutoApplied && (
              <span style={{ background: '#D4A017', color: '#0f172a', fontSize: '9px', fontWeight: 800, padding: '2px 6px', borderRadius: '6px', textTransform: 'uppercase' }} data-testid="promo-auto-applied">
                Auto-toegepast
              </span>
            )}
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <input
              type="text"
              value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))}
              placeholder="Bijv. ANNAX9K2P"
              data-testid="discount-code-input"
              style={{
                flex: 1, padding: '10px 12px', borderRadius: '8px',
                border: '1px solid #e2e8f0', fontSize: '14px', fontFamily: 'monospace',
                fontWeight: 700, color: '#0f172a', letterSpacing: '0.05em',
                background: discountValidation?.valid ? '#f0fdf4' : 'white',
              }}
            />
            {discountInput && (
              <button
                type="button"
                onClick={removeDiscount}
                data-testid="discount-code-clear"
                style={{
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px',
                  padding: '0 12px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
          {discountChecking && (
            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RefreshCw size={11} className="animate-spin" /> Code controleren…
            </p>
          )}
          {!discountChecking && discountValidation && (
            <p
              data-testid={`discount-code-status-${discountValidation.valid ? 'valid' : 'invalid'}`}
              style={{
                fontSize: '12px',
                marginTop: '6px',
                color: discountValidation.valid ? '#059669' : '#dc2626',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {discountValidation.valid ? (
                <>
                  <CheckCircle size={12} /> Code geldig — {' '}
                  {discountValidation.discount_type === 'percentage'
                    ? `${discountValidation.discount_value}% korting`
                    : `€${discountValidation.discount_value} korting`}
                  {' '}eerste {discountValidation.valid_first_x_months} mnd
                </>
              ) : (
                <>
                  <AlertCircle size={12} /> {discountValidation.reason || 'Ongeldige code'}
                </>
              )}
            </p>
          )}
          {discountValidation?.valid && (
            <div style={{ marginTop: '10px', padding: '10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#065f46' }}>
                <span>Oorspronkelijk:</span>
                <span style={{ textDecoration: 'line-through' }}>{formatEUR(discountValidation.original_ex)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#065f46', fontWeight: 700, marginTop: '2px' }}>
                <span>Korting:</span>
                <span>− {formatEUR(discountValidation.discount_amount_ex)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#0f172a', fontWeight: 800, borderTop: '1px solid #bbf7d0', paddingTop: '6px', marginTop: '4px' }}>
                <span>Nieuwe prijs (ex btw):</span>
                <span>{formatEUR(discountValidation.discounted_ex)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Managed AI transition acceptance */}
      {isManaged && (
        <div style={{ maxWidth: '600px', margin: '0 auto 16px', background: 'white', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '14px' }}>
          <label data-testid="accept-transition-label" style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox"
              data-testid="accept-transition-checkbox"
              checked={acceptTransition}
              onChange={e => setAcceptTransition(e.target.checked)}
              style={{ marginTop: '3px', width: '16px', height: '16px', accentColor: '#D4A017' }}
            />
            <span style={{ fontSize: '12px', color: '#0f172a', lineHeight: 1.5 }}>
              Ik begrijp dat ik nu <strong>€995,95 excl. btw (€{(995.95 * 1.21).toFixed(2).replace('.', ',')} incl. btw)</strong> betaal voor de eerste maand (onboarding + launch). Na 30 dagen gaat mijn abonnement automatisch over op <strong>€2.495,95/mnd excl. btw</strong>. Ik kan vóór de overgang opzeggen vanuit mijn klantdashboard.
            </span>
          </label>
        </div>
      )}

      {/* Payment methods info */}
      <div style={{ maxWidth: '600px', margin: '0 auto 20px', background: '#f8fafc', borderRadius: '10px', padding: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Info size={14} color="#64748b" />
        <p style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.4 }}>
          Betaalmethoden via Stripe: iDEAL · Creditcard · SEPA Direct Debit · Bancontact. Uw betaalgegevens worden nooit door ons opgeslagen — alles wordt afgehandeld op de beveiligde Stripe-pagina.
        </p>
      </div>

      {/* CTA */}
      {/* Billing info check (alleen voor subscriptions) */}
      {billingInfo && (
        <div style={{ maxWidth: '600px', margin: '0 auto 16px' }}>
          {!billingInfo.is_complete_for_checkout ? (
            <div data-testid="billing-warning" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '4px solid #f59e0b', borderRadius: 10, padding: '12px 14px' }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>Bedrijfsgegevens incompleet</p>
              <p style={{ fontSize: 11, color: '#78350f', marginTop: 2 }}>Ontbreekt: {billingInfo.missing_required.join(', ')}.</p>
              <a href="/platform/billing-info" data-testid="billing-fix-link" style={{ display: 'inline-block', marginTop: 8, background: '#D4A017', color: '#0f172a', fontSize: 11, fontWeight: 800, padding: '6px 12px', borderRadius: 6, textDecoration: 'none' }}>Aanvullen →</a>
            </div>
          ) : (
            <div data-testid="billing-ok" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
              <p style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Factuur naar:</p>
              <p style={{ fontSize: 13, color: '#0f172a', fontWeight: 700 }}>{billingInfo.company.name}</p>
              <p style={{ fontSize: 12, color: '#64748b' }}>{billingInfo.company.address}, {billingInfo.company.postal_code} {billingInfo.company.city}</p>
              <p style={{ fontSize: 11, color: '#64748b' }}>KvK: {billingInfo.company.kvk_number} {billingInfo.company.vat_number && `· BTW: ${billingInfo.company.vat_number}`}</p>
              <a href="/platform/billing-info" style={{ fontSize: 11, color: '#D4A017', textDecoration: 'none', marginTop: 4, display: 'inline-block' }}>Wijzigen</a>
            </div>
          )}
        </div>
      )}

      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <button
          data-testid="checkout-btn"
          onClick={handleCheckout}
          disabled={loading || needsTransitionAccept || (billingInfo && !billingInfo.is_complete_for_checkout)}
          style={{
            width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
            background: isManaged
              ? 'linear-gradient(135deg, #D4A017, #b8860b)'
              : 'linear-gradient(135deg, #0f172a, #1e1b4b)',
            color: isManaged ? '#0f172a' : 'white',
            fontSize: '15px', fontWeight: 800, cursor: (loading || needsTransitionAccept) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            opacity: (loading || needsTransitionAccept || (billingInfo && !billingInfo.is_complete_for_checkout)) ? 0.5 : 1,
          }}
        >
          {loading ? (
            <><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Verwerken...</>
          ) : (
            <>
              {isManaged
                ? `Start eerste maand — €${effectiveExVat.toFixed(2).replace('.', ',')} excl. btw`
                : `Activeer voor €${effectiveExVat.toFixed(2).replace('.', ',')} excl. btw`} <ArrowRight size={16} />
            </>
          )}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '12px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#94a3b8' }}>
            <Shield size={11} /> Veilig betalen
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#94a3b8' }}>
            <Lock size={11} /> SSL encrypted
          </span>
          <span style={{ fontSize: '11px', color: '#94a3b8' }}>Maandelijks opzegbaar</span>
        </div>
      </div>
    </div>
  );
}
